import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { callApi } from '../../util/apiWrapper';
import GeneralItems from './ChecklistComponents/GeneralItems';
import PostSecondaryEducationItems from './ChecklistComponents/PostSecondaryEducationItems';
import PostSecondaryCareerItems from './ChecklistComponents/PostSecondaryCareerItems';
import MilitaryItems from './ChecklistComponents/MilitaryItems';
import JobCorpsItems from './ChecklistComponents/JobCorpsItems';
import WorkforceItems from './ChecklistComponents/WorkforceItems';
import { appInsights, logException } from '../../services/appInsights';
import RequiredInfoDialog from '../Shared/RequiredInfoDialog';
import CloseIcon from '@material-ui/icons/Close';
import ConfirmSubmitDialog from './ChecklistDialogs/ConfirmSubmitDialog';
import { webPrimaryColor } from '../../assets/jss/nextjs-material-kit-pro.js';
import PleaseWait from '../Shared/PleaseWait';
import useAuthRedirect from '../../hooks/authRedirectHook';
import { getPostGraduationOptionLabel, OLD_POST_GRADUATION_OPTIONS, POST_GRADUATION_OPTIONS } from '../../constants/general';
import formStyles from './formStyles';
import { computeConfirmedPointsEarned } from './computeConfirmedPointsEarned';
import { FORM_SCHEMA } from './checklistFormHelper';
import { generalChecklistItems } from '../../constants/generalChecklistItems';
import { postSecondaryEducationChecklistItems } from '../../constants/postSecondaryEducationChecklistItems';
import { postSecondaryCareerChecklistItems } from '../../constants/postSecondaryCareerChecklistItems';
import { militaryChecklistItems } from '../../constants/militaryChecklistItems';
import { jobCorpsChecklistItems } from '../../constants/jobCorpsChecklistItems';
import { workforceChecklistItems } from '../../constants/workforceChecklistItems';
import { collegeChecklistItems } from '../../constants/collegeChecklistItems';
import { careerTrainingChecklistItems } from '../../constants/careerTrainingChecklistItems';
import { getChecklistItems, getStartIndices, sumPoints, displayStatus, isAdmin, isStudent, isAlumni, isFHT } from '../../util/miscUtil';
import OptionItems from './ChecklistComponents/OptionItems';

const SeniorChecklist = observer((props) => {
  const store = props.SeniorStore;
  const auth = props.AuthStore;

  const { handleSaveReturn } = props;

  const formClasses = formStyles();

  const [requiredFields, setRequiredFields] = useState(null);
  const [requiredFieldsIntro, setRequiredFieldsIntro] = useState('');
  const [showRequiredFieldsDialog, setShowRequiredFieldsDialog] = useState(false);
  const [originalStatus, setOriginalStatus] = useState('');
  const [warning, setWarning] = useState('');
  const [showWarning, setShowWarning] = useState(false);
  const [continueSubmit, setContinueSubmit] = useState(false);

  const [totalPoints, setTotalPoints] = useState(0);
  const [generalPoints, setGeneralPoints] = useState(0);
  const [secedPoints, setSecedPoints] = useState(0);
  const [secCareerPoints, setSecCareerPoints] = useState(0);
  const [militaryPoints, setMilitaryPoints] = useState(0);
  const [jobcorpsPoints, setJobcorpsPoints] = useState(0);
  const [workforcePoints, setWorkforcePoints] = useState(0);
  const [collegePoints, setCollegePoints] = useState(0);
  const [careerTrainingPoints, setCareerTrainingPoints] = useState(0);

  const [showConfirmSubmitDialog, setShowConfirmSubmitDialog] = useState(false);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowWarning(false);
  };

  const handleConfirmSubmitDialogClose = () => {
    setShowConfirmSubmitDialog(false);
  };

  const action = (
    <React.Fragment>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </React.Fragment>
  );

  // prettier-ignore
  const [form, setForm] = useState(FORM_SCHEMA);

  const [graduationOptions, setGraduationOptions] = useState(POST_GRADUATION_OPTIONS);

  useEffect(() => {
    // Use OLD_POST_GRADUATION_OPTIONS only if the version is 1, otherwise default to POST_GRADUATION_OPTIONS
    const version = store?.checklistObj?.version;
    setGraduationOptions(version === 1 ? OLD_POST_GRADUATION_OPTIONS : POST_GRADUATION_OPTIONS);
    if (version === 1) {
      setGraduationOptions(OLD_POST_GRADUATION_OPTIONS);
      setForm({ ...FORM_SCHEMA, version: version });
    }
  }, [store?.checklistObj?.version]);

  // Redirect user to login page if token is no longer available.
  useAuthRedirect(auth);

  // Load checklist if one exists
  useEffect(() => {
    window.scrollTo({ top: 0 });

    const fetchChecklist = async (studentId) => {
      try {
        store.setLoading(true);
        store.setSearching(true);
        const checklist = await callApi(auth, 'checklist/' + studentId, 'GET');
        // If checklist fetched, set it to form.
        if (checklist.result) {
          store.setChecklistObj(checklist.result);
          // Merge loaded data with FORM_SCHEMA to ensure all fields exist
          const loadedData = JSON.parse(checklist.result.checklist_data);
          setForm({ ...FORM_SCHEMA, ...loadedData, version: checklist.result.version });
          setOriginalStatus(loadedData.status);
        }
        store.setSearching(false);
        store.setLoading(false);
      } catch (error) {
        store.setSearching(false);
        store.setLoading(false);
        console.warn('Error fetching checklist:', error);
        logException(error);
      }
    };

    fetchChecklist(store.senior.id);
  }, [auth, store]);

  useEffect(() => {
    computeTotalPoints();

    const canSubmit = () => {
      let rc = true;
      let requiredFields = [];

      if (form.postGraduationPlan.length === 0 || form.postGraduationPlan.toLowerCase() === 'undecided') {
        requiredFields = [...requiredFields, { requirement: 'Indicate your post-graduation plans before submitting your checklist.' }];
        rc = false;
      }

      setRequiredFieldsIntro(
        'You have not completed all of the required information on your Checklist. Please make these adjustments before submitting.'
      );
      setRequiredFields(requiredFields);
      return rc;
    };

    if (continueSubmit) {
      if (canSubmit()) {
        async function submitChecklist() {
          try {
            store.setLoading(false);

            let updatedChecklist = null;

            form.status = 'submitted';
            form.dispositionReason = '';

            if (store.checklistObj && store.checklistObj.id && store.checklistObj.id > 0) {
              updatedChecklist = store.checklistObj;
              updatedChecklist.checklist_data = form;
              updatedChecklist.updated_at = new Date();
              updatedChecklist.updated_by = auth && auth.user && auth.user.result.id;
            }

            if (updatedChecklist) {
              updatedChecklist = await callApi(auth, 'checklist', 'PUT', {
                item: updatedChecklist,
              });
            } else {
              const res = await callApi(auth, 'checklist', 'POST', {
                item: {
                  student_id: store.senior.id,
                  created_at: new Date(),
                  created_by: store.senior.userId,
                  updated_at: new Date(),
                  updated_by: store.senior.userId,
                  checklist_data: null,
                },
              });

              if (res.result) updatedChecklist = res.result;

              // PUT to update checklist_data in correct format
              updatedChecklist.checklist_data = form;
              const c = await callApi(auth, 'checklist', 'PUT', {
                item: updatedChecklist,
              });

              if (c.result) {
                updatedChecklist = res.result;
                store.setChecklistObj(updatedChecklist);
              }
            }

            appInsights.trackTrace({ message: `Checklist submitted by ${auth && auth.user && auth.user.result.email}` });

            store.setLoading(false);
            window.scrollTo({ top: 0 });
            handleSaveReturn();
          } catch (error) {
            logException(error);
            store.setLoading(false);
            console.log('Error submitting updated checklist:', error);
          }
        }
        submitChecklist();
      } else {
        setShowRequiredFieldsDialog(true);
      }
    }
  }, [continueSubmit, auth, store, form, handleSaveReturn]);

  const sendChecklistReturnedEmail = async (id) => {
    try {
      store.setLoading(true);
      await callApi(auth, 'checklistreturnemail', 'POST', { student_id: id });
      appInsights.trackTrace({ message: `Checklist returned email sent to ${auth && auth.user && auth.user.result.email}` });
      store.setLoading(false);
    } catch (error) {
      logException(error);
      store.setLoading(false);
      console.log('Error sending Checklist returned email:', error);
    }
  };

  const handleSaveChecklist = () => {
    // if admin is returning the checklist, require a reason.
    if (isAdmin(auth.roles)) {
      if (form.status === 'returned') {
        if (!form.dispositionReason || form.dispositionReason.length === 0) {
          setWarning('You must provide a reason for returning the checklist.');
          setShowWarning(true);
          return;
        }
      }
    }

    async function writeChecklist() {
      try {
        store.setLoading(false);

        // Send email
        if (originalStatus !== 'returned' && form.status === 'returned') {
          sendChecklistReturnedEmail(store.senior.id);
        }

        let updatedChecklist = null;

        if (store.checklistObj && store.checklistObj.id && store.checklistObj.id > 0) {
          updatedChecklist = store.checklistObj;
          updatedChecklist.checklist_data = form;
          updatedChecklist.updated_at = new Date();
          updatedChecklist.updated_by = auth && auth.user && auth.user.result.id;
        }

        if (updatedChecklist) {
          updatedChecklist = await callApi(auth, 'checklist', 'PUT', {
            item: updatedChecklist,
          });
        } else {
          const res = await callApi(auth, 'checklist', 'POST', {
            item: {
              student_id: store.senior.id,
              created_at: new Date(),
              created_by: store.senior.userId,
              updated_at: new Date(),
              updated_by: store.senior.userId,
              checklist_data: null,
            },
          });

          // PUT to update checklist_data in the correct format
          if (res.result) updatedChecklist = { ...res.result, checklist_data: form };
          const c = await callApi(auth, 'checklist', 'PUT', {
            item: updatedChecklist,
          });

          if (c.result) {
            updatedChecklist = res.result;
            store.setChecklistObj(updatedChecklist);
          }
        }

        appInsights.trackTrace({ message: `Checklist saved by ${auth && auth.user && auth.user.result.email}` });

        store.setLoading(false);
        handleSaveReturn();
      } catch (error) {
        logException(error);
        store.setLoading(false);
        console.log('Error writing updated checklist:', error);
      }
    }
    writeChecklist();
  };

  const handleRequiredInfoClose = () => {
    setShowRequiredFieldsDialog(false);
  };

  const showSaveButton = () => {
    // Status Values: created, submitted, approved, returned
    if (isAdmin(auth.roles)) {
      return true;
    }
    if (isStudent(auth.roles) || isAlumni(auth.roles)) {
      if (form.status === 'created' || form.status === 'returned') {
        return true;
      }
    }
    return false;
  };

  const showDisposition = () => {
    let rc = false;
    if (isAdmin(auth.roles)) {
      // Status Values: created, submitted, approved, returned
      // PS-355 if (originalStatus === 'submitted') {
      rc = true;
      // PS-355 }
    }
    return rc;
  };

  const isPostGraduationPlanDisabled = () => {
    if (isStudent(auth.roles)) {
      if (form.status === 'created' || form.status === 'returned') {
        return false;
      }
    }
    return true;
  };

  const shouldDisplayStatusNotes = () => {
    if (form.dispositionReason && form.dispositionReason.length > 0) return true;
    return false;
  };

  const computeTotalPoints = () => {
    // Dynamically sum points from checklist item arrays
    const general = sumPoints(generalChecklistItemsToUse);
    const seced = sumPoints(postSecondaryEducationChecklistItemsToUse);
    const secCareer = sumPoints(postSecondaryCareerChecklistItemsToUse);
    const military = sumPoints(militaryChecklistItemsToUse);
    const jobcorps = sumPoints(jobCorpsChecklistItemsToUse);
    const workforce = sumPoints(workforceChecklistItemsToUse);
    const college = sumPoints(collegeChecklistItemsToUse);
    const careerTraining = sumPoints(careerTrainingChecklistItemsToUse);

    let grandTotal = general;
    if (form.postGraduationPlan && form.postGraduationPlan.length > 0) {
      if (form.postGraduationPlan === 'post-secondary education') {
        grandTotal += seced;
      } else if (form.postGraduationPlan === 'post-secondary career training') {
        grandTotal += secCareer;
      } else if (form.postGraduationPlan === 'military') {
        grandTotal += military;
      } else if (form.postGraduationPlan === 'Job Corps/AmeriCorps') {
        grandTotal += jobcorps;
      } else if (form.postGraduationPlan === 'workforce') {
        grandTotal += workforce;
      } else if (form.postGraduationPlan === '4_year_college' || form.postGraduationPlan === '2_year_college') {
        grandTotal += college;
      } else if (form.postGraduationPlan === 'career_training') {
        grandTotal += careerTraining;
      }
    }

    setGeneralPoints(general);
    setSecedPoints(seced);
    setSecCareerPoints(secCareer);
    setMilitaryPoints(military);
    setJobcorpsPoints(jobcorps);
    setWorkforcePoints(workforce);
    setCollegePoints(college);
    setCareerTrainingPoints(careerTraining);
    setTotalPoints(grandTotal);
  };

  const isAdminDisabled = (val) => {
    return !!(isStudent(auth.roles) || isAlumni(auth.roles) || isFHT(auth.roles));
  };

  const version = store?.checklistObj?.version || 2;

  const generalChecklistItemsToUse = getChecklistItems(generalChecklistItems, { includeInstructions: true }, version);
  const postSecondaryEducationChecklistItemsToUse = getChecklistItems(postSecondaryEducationChecklistItems, { includeInstructions: true }, version);
  const postSecondaryCareerChecklistItemsToUse = getChecklistItems(postSecondaryCareerChecklistItems, { includeInstructions: true }, version);
  const militaryChecklistItemsToUse = getChecklistItems(militaryChecklistItems, { includeInstructions: true }, version);
  const jobCorpsChecklistItemsToUse = getChecklistItems(jobCorpsChecklistItems, { includeInstructions: true }, version);
  const workforceChecklistItemsToUse = getChecklistItems(workforceChecklistItems, { includeInstructions: true }, version);
  const collegeChecklistItemsToUse = getChecklistItems(collegeChecklistItems, { includeInstructions: true }, version);
  const careerTrainingChecklistItemsToUse = getChecklistItems(
    careerTrainingChecklistItems,
    { includeInstructions: true, includeGroupMembers: true },
    version
  );

  // For numbering, exclude instructions
  const generalChecklistItemsForNumbering = getChecklistItems(generalChecklistItems, { includeInstructions: true }, version);
  const postSecondaryEducationChecklistItemsForNumbering = getChecklistItems(
    postSecondaryEducationChecklistItems,
    { includeInstructions: false },
    version
  );
  const postSecondaryCareerChecklistItemsForNumbering = getChecklistItems(postSecondaryCareerChecklistItems, { includeInstructions: false }, version);
  const collegeChecklistItemsForNumbering = getChecklistItems(collegeChecklistItems, { includeInstructions: false }, version);
  const careerTrainingChecklistItemsForNumbering = getChecklistItems(careerTrainingChecklistItems, { includeInstructions: false }, version);
  const militaryChecklistItemsForNumbering = getChecklistItems(militaryChecklistItems, { includeInstructions: false }, version);
  const jobCorpsChecklistItemsForNumbering = getChecklistItems(jobCorpsChecklistItems, { includeInstructions: false }, version);
  const workforceChecklistItemsForNumbering = getChecklistItems(workforceChecklistItems, { includeInstructions: false }, version);

  // Use these for numbering only
  const sectionsForNumbering = [
    generalChecklistItemsForNumbering,
    postSecondaryEducationChecklistItemsForNumbering,
    postSecondaryCareerChecklistItemsForNumbering,
    collegeChecklistItemsForNumbering,
    careerTrainingChecklistItemsForNumbering,
    militaryChecklistItemsForNumbering,
    jobCorpsChecklistItemsForNumbering,
    workforceChecklistItemsForNumbering,
  ];

  const indices = getStartIndices(sectionsForNumbering, 1);
  const [
    generalStartIndex,
    postSecondaryEducationStartIndex,
    postSecondaryCareerStartIndex,
    collegeChecklistItemsStartIndex,
    careerTrainingChecklistItemsStartIndex,
    militaryStartIndex,
    ...rest
  ] = indices;

  let jobCorpsStartIndex, workforceStartIndex;
  if (version === 1) {
    [jobCorpsStartIndex, workforceStartIndex] = rest;
  } else {
    [workforceStartIndex] = rest;
  }

  const selectValue = graduationOptions.some((opt) => opt.value === form.postGraduationPlan) ? form.postGraduationPlan : '';

  return (
    <div style={{ width: '90%', margin: '0 25px 0 20px' }}>
      <PleaseWait isLoading={store.loading} />
      <div className={formClasses.unprintedContent + ' ' + formClasses.floatingScore}>
        <div className={formClasses.floatingScoreNumber}>
          {computeConfirmedPointsEarned(form)}/{totalPoints}
        </div>
        <div className={formClasses.floatingScoreLabel}>points</div>
      </div>
      <div className={formClasses.printedContent + ' ' + formClasses.printedScore}>Score: {computeConfirmedPointsEarned(form)}/100 points</div>
      {store.senior && (
        <div>
          <Grid container align="left" className={formClasses.unprintedContent}>
            <Grid item xs={12} align="center" className={formClasses.textRowLabelSpacer} style={{ minHeight: 80 }}>
              <aside style={{ fontWeight: 600 }}>Current Status: {form.status && displayStatus(form.status)}</aside>
              {isStudent(auth.roles) && form.status === 'returned' && (
                <aside className={formClasses.error}>
                  NOTE: This Checklist was returned for additional information. Please make the necessary changes and re-submit.
                </aside>
              )}
            </Grid>
          </Grid>
          <div className={formClasses.printedContent}>
            <aside style={{ fontWeight: 600, fontSize: '1.50rem', paddingBottom: 10 }} align="center">
              Current Status: {form.status && displayStatus(form.status)}
            </aside>
            {isStudent(auth.roles) && form.status === 'returned' && (
              <aside className={formClasses.error}>
                NOTE: This Checklist was returned for additional information. Please make the necessary changes and re-submit.
              </aside>
            )}
          </div>
        </div>
      )}
      {shouldDisplayStatusNotes() && (
        <Grid item xs={12} align="left" className={formClasses.dispositionNote}>
          {form.dispositionReason && form.dispositionReason.length > 0 && (
            <React.Fragment>
              <summary style={{ fontWeight: 600 }}>Notes Regarding Status</summary>
              <summary>{form.dispositionReason}</summary>
            </React.Fragment>
          )}
        </Grid>
      )}
      <Grid container direction="row" align="left" className={formClasses.unprintedContent}>
        <Grid item xs={12} sm={4} md={3} lg={2} className={formClasses.labelRowSpacer}>
          <InputLabel id="post-graduation-plan">My post-graduation plan is</InputLabel>
        </Grid>
        <Grid item xs={11} sm={5} md={4} lg={3} className={formClasses.textRowSpacer}>
          <FormControl fullWidth>
            <Select
              labelId="post-graduation-plan"
              id="post-graduation"
              value={selectValue}
              onChange={(event) => setForm({ ...form, postGraduationPlan: event.target.value })}
              disabled={isPostGraduationPlanDisabled()}
            >
              {graduationOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={false} sm={3} md={5} lg={7} className={formClasses.textRowSpacer}></Grid>
      </Grid>
      <summary className={formClasses.printedContent + ' ' + formClasses.spacer2520}>
        My post-graduation plan is {getPostGraduationOptionLabel(form.postGraduationPlan)}.
      </summary>
      <Grid container direction="row" align="left">
        <Grid item xs={12} className={formClasses.labelRowSpacer}>
          <span style={{ fontStyle: 'italic' }}>
            Before submitting, please make sure this plan matches the one submitted as part of the scholarship application.
          </span>
        </Grid>
        <Grid item sm={3} md={5} lg={7} className={formClasses.textRowSpacer}></Grid>
      </Grid>
      <Grid container key={store.senior.userId} align="left">
        <Grid container direction="row" className={formClasses.unprintedContent + ' ' + formClasses.columnHeaders}>
          <Grid item xs={12} sm={12} md={8} lg={8}>
            <section align="left" className={formClasses.columnHeader}>
              Activity
            </section>
          </Grid>
          <Grid item xs={3} sm={3} md={1} lg={1}>
            <section align="center" className={formClasses.columnHeader}>
              Points
            </section>
          </Grid>
          <Grid item xs={6} sm={6} md={2} lg={2}>
            <section align="left" className={formClasses.columnHeader}>
              Deadline
            </section>
          </Grid>
          <Grid item xs={3} sm={3} md={1} lg={1}>
            <section align="center" className={formClasses.columnHeader}>
              Confirmed
            </section>
          </Grid>
        </Grid>
        {store.senior && store.senior.user && (
          <GeneralItems
            className={formClasses.topMargin10}
            form={form}
            setForm={setForm}
            auth={auth}
            emailAddress={store.senior.user.email}
            alumni={store.alumni}
            startIndex={generalStartIndex}
            points={generalPoints}
            formClasses={formClasses}
            items={generalChecklistItemsToUse}
          />
        )}
        {store.senior && (
          <PostSecondaryEducationItems
            className={formClasses.topMargin10}
            form={form}
            setForm={setForm}
            auth={auth}
            act={store.senior.act}
            alumni={store.alumni}
            startIndex={postSecondaryEducationStartIndex}
            points={secedPoints}
            formClasses={formClasses}
            items={postSecondaryEducationChecklistItemsToUse}
          />
        )}
        <PostSecondaryCareerItems
          className={formClasses.topMargin10}
          form={form}
          setForm={setForm}
          auth={auth}
          act={store.senior.act}
          alumni={store.alumni}
          startIndex={postSecondaryCareerStartIndex}
          points={secCareerPoints}
          formClasses={formClasses}
          items={postSecondaryCareerChecklistItemsToUse}
        />
        <OptionItems
          className={formClasses.topMargin10}
          form={form}
          setForm={setForm}
          auth={auth}
          startIndex={collegeChecklistItemsStartIndex}
          points={collegePoints}
          formClasses={formClasses}
          items={collegeChecklistItemsToUse}
          title="COLLEGE OPTION"
          subTitle="Complete this section if you are planning to attend a 4-year or 2-year college after high school"
          actScore={store?.senior?.act}
        />
        <OptionItems
          className={formClasses.topMargin10}
          form={form}
          setForm={setForm}
          auth={auth}
          startIndex={careerTrainingChecklistItemsStartIndex}
          points={careerTrainingPoints}
          formClasses={formClasses}
          items={careerTrainingChecklistItemsToUse}
          title="CAREER TRAINING OPTION"
          subTitle="Complete this section if you are planning to enter a trade by obtaining a certificate, a license, an apprenticeship, or any vocational training"
          actScore={store?.senior?.act}
        />
        <MilitaryItems
          className={formClasses.topMargin10}
          form={form}
          setForm={setForm}
          auth={auth}
          asvabScore={store?.senior?.asvab}
          alumni={store.alumni}
          startIndex={militaryStartIndex}
          points={militaryPoints}
          formClasses={formClasses}
          items={militaryChecklistItemsToUse}
        />
        {version === 1 && (
          <JobCorpsItems
            className={formClasses.topMargin10}
            form={form}
            setForm={setForm}
            auth={auth}
            alumni={store.alumni}
            startIndex={jobCorpsStartIndex}
            points={jobcorpsPoints}
            formClasses={formClasses}
            items={jobCorpsChecklistItemsToUse}
          />
        )}
        <WorkforceItems
          className={formClasses.topMargin10}
          form={form}
          setForm={setForm}
          auth={auth}
          alumni={store.alumni}
          startIndex={workforceStartIndex}
          points={workforcePoints}
          formClasses={formClasses}
          items={workforceChecklistItemsToUse}
        />
        {showDisposition() && (
          <React.Fragment>
            <Grid
              container
              direction="row"
              className={formClasses.unprintedContent}
              style={{ marginTop: 20, padding: 20, border: '1px solid darkgreen', background: '#dcf5dc' }}
            >
              <Grid item xs={12} align="center">
                <label style={{ fontWeight: 600 }}>Successful Futures Administration Only</label>
              </Grid>
              <Grid item xs={12} sm={5} md={3} lg={2} className={formClasses.textRowLabelSpacer}>
                <label style={{ fontWeight: 600, color: '#000000' }}>Application Status</label>
              </Grid>
              <Grid item xs={12} sm={12} md={9} lg={10} className={formClasses.textArea5RowSpacer}>
                <FormControl fullWidth>
                  <RadioGroup
                    row
                    aria-label="approve checklist"
                    name="approve-checklist"
                    horizontal="true"
                    value={form.status}
                    onChange={(event) => setForm({ ...form, status: event.target.value })}
                    disabled={isAdminDisabled('returned/approved radio group')}
                  >
                    <FormControlLabel
                      value="returned"
                      className={formClasses.marginRight40}
                      control={<Radio color="default" disabled={isAdminDisabled('returned')} />}
                      label={
                        <span className={formClasses.radioLabel} style={{ fontWeight: 500, color: webPrimaryColor }}>
                          Returned
                        </span>
                      }
                    />
                    <FormControlLabel
                      value="approved"
                      className={formClasses.marginRight40}
                      control={<Radio color="default" disabled={isAdminDisabled('approved')} />}
                      label={
                        <span className={formClasses.radioLabel} style={{ fontWeight: 500, color: '#000000' }}>
                          Approved
                        </span>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <React.Fragment>
                <Grid item xs={12} className={formClasses.textRowSpacer}>
                  <FormControl fullWidth>
                    <TextField
                      id="disposition-reason"
                      size="small"
                      fullWidth
                      label={`Enter any notes for status '${form.status && displayStatus(form.status)}'`}
                      variant="outlined"
                      value={form.dispositionReason}
                      onChange={(event) => setForm({ ...form, dispositionReason: event.target.value })}
                      disabled={isAdminDisabled('status notes')}
                      InputProps={{ maxLength: 500, classes: { input: formClasses.textField } }}
                      className={formClasses.root}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} className={formClasses.textRowSpacer}>
                  <label className={formClasses.note}>
                    {form.status === 'returned'
                      ? 'This reason will be displayed to the student when they view the returned checklist.'
                      : 'This reason will be displayed to the student when they view the checklist.'}
                  </label>
                </Grid>
              </React.Fragment>
            </Grid>
          </React.Fragment>
        )}

        <Grid container direction="row" className={formClasses.unprintedContent} style={{ width: '95vw' }}>
          <Grid item xs={12} sm={8} md={5} lg={7} className={formClasses.buttonSpacer}>
            {(!form.status || form.status === 'created' || form.status === 'returned') && (
              <Tooltip title="Click to submit your checklist for review." placement="top">
                <Button
                  className={formClasses.buttonWidth}
                  color="secondary"
                  variant="contained"
                  onClick={(event) => setShowConfirmSubmitDialog(true)}
                  disabled={!isStudent(auth.roles)}
                  style={{ margin: '0 0 0 10px' }}
                >
                  Submit
                </Button>
              </Tooltip>
            )}
          </Grid>
          <Grid item xs={12} sm={4} md={2} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={4} align="right" className={formClasses.buttonSpacer + ' ' + formClasses.unprintedContent}>
            <Button className={formClasses.buttonWidth} variant="contained" onClick={(event) => handleSaveReturn()} style={{ margin: '0 20px 0 0' }}>
              {isFHT(auth.roles) ? 'Close' : 'Cancel'}
            </Button>
            {showSaveButton() && (
              <Button
                className={formClasses.buttonWidth}
                color="primary"
                variant="contained"
                onClick={(event) => handleSaveChecklist()}
                disabled={!store.senior || isFHT(auth.roles)}
              >
                Save
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>

      <RequiredInfoDialog
        title="Checklist Errors"
        introduction={requiredFieldsIntro}
        content={requiredFields}
        showDialog={showRequiredFieldsDialog}
        handleClose={handleRequiredInfoClose}
      />
      <Snackbar
        ContentProps={{
          classes: {
            root: formClasses.warning,
          },
        }}
        open={showWarning}
        autoHideDuration={6000}
        onClose={handleClose}
        message={warning}
        action={action}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      ></Snackbar>
      <ConfirmSubmitDialog
        setContinueSubmit={setContinueSubmit}
        setShowConfirmSubmitDialog={setShowConfirmSubmitDialog}
        showDialog={showConfirmSubmitDialog}
        handleClose={handleConfirmSubmitDialogClose}
      />
    </div>
  );
});

export default SeniorChecklist;
